import axios from "axios";
import { toast } from "react-hot-toast";
import { getAccessToken, getRefreshToken, logout, setAccessToken } from "../auth/tokenStorage";
import {
  AgentGuidance,
  AgentGuidancesParams,
  AgentGuidancesResponse,
  AgentPipelineResponse,
  CreateAgentGuidanceRequest,
  FeedbackStatusEnum,
  FeedbackTypeEnum,
  UpdateAgentCronResponse,
  UpdateAgentPipelinePayload,
  UpdateAgentPipelineResponse
} from "types";
import i18n from "i18n/config";

declare global {
  interface Window {
    appConfig: {
      apiUrl: string;
    };
  }
}

const api = axios.create({
  baseURL: window.appConfig.apiUrl,
});

const translateMessage = (
  messageKey: string | undefined,
  type: "error" | "success" | "unknown" = "unknown"
): string => {
  if (!messageKey) {
    return (i18n as any).t(`fallback.${type}`) as string;
  }
  const translationKey = `apiResponseMessages.${messageKey}`;
  const translatedMessage = (i18n as any).t(translationKey) as string;

  if (translatedMessage && translatedMessage !== translationKey) {
    return translatedMessage;
  }

  return (i18n as any).t(`fallback.${type}`) as string;
};

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => {
    const method = res.config.method;
    if (
      method === "post" ||
      method === "put" ||
      method === "delete" ||
      method === "patch"
    ) {
      const { data, status } = res;

      if (status >= 400 && (data?.message_code || data?.detail)) {
        const errorMessage = translateMessage(
          data?.message_code || data?.detail,
          "error"
        );
        toast.error(errorMessage);
      } else if (data?.message_code) {
        const successMessage = translateMessage(data.message_code, "success");
        toast.success(successMessage);
      }
    }
    return res;
  },
  async (error) => {
    const originalRequest = error.config;
    const method = originalRequest.method;
    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = getRefreshToken();

      if (refreshToken?.length > 0) {
        originalRequest._retry = true;

        try {
          const res = await api.post("/auth/refresh-token", {
            refresh_token: refreshToken,
          });
          const newToken = res.data.access_token;
          setAccessToken(newToken, res.data.expires_in);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        } catch {
          logout();
        }
      } else {
        logout();
      }
    } else if (error.response?.status === 401) {
      logout();
    }

    if (
      method === "post" ||
      method === "put" ||
      method === "delete" ||
      method === "patch"
    ) {
      const errorData = error.response?.data;
      if (errorData?.message_code || errorData?.detail) {
        const errorMessage = translateMessage(
          errorData?.message_code || errorData?.detail,
          "error"
        );
        toast.error(errorMessage);
      }
    }

    return Promise.resolve(error.response);
  }
);

export default api;

export async function fetchRoles({
  take,
  skip,
}: {
  take: number;
  skip: number;
}) {
  const response = await api.get("/users/groups", { params: { take, skip } });
  return response.data;
}

export async function fetchAllUsersAPI({
  take,
  skip,
  handler,
  signal,
}: {
  take: number;
  skip: number;
  handler?: any;
  signal?: AbortSignal;
}) {
  const params = handler ? { handler } : {};
  const response = await api.get("/users", {
    params: { take, skip, ...params },
    ...(signal instanceof AbortSignal ? { signal } : {}),
  });
  return response.data;
}

export async function fetchUsersCurrentWorkspaceAPI({
  take,
  skip,
  handler,
  signal,
}: {
  take: number;
  skip: number;
  handler?: any;
  signal?: AbortSignal;
}) {
  const params = handler ? { handler } : {};
  const response = await api.get("/workspaces/users", {
    params: { take, skip, ...params },
    ...(signal instanceof AbortSignal ? { signal } : {}),
  });
  return response.data;
}

// Chat Thread APIs
export async function createThread(data: {
  id?: string;
  agentId: string;
  title: string;
  archived?: boolean;
}) {
  const response = await api.post("/chat/threads", data);
  return response.data;
}

export async function listThreads(params?: {
  agent_id: string;
  take: number;
  skip: number;
}) {
  const response = await api.get("/chat/threads", { params });
  return response.data;
}

export async function getThread(threadId: string, handler?: string) {
  const params = handler ? { handler } : {};
  const response = await api.get(`/chat/threads/${threadId}`, { params });
  return response.data;
}

export async function updateThread(
  threadId: string,
  data: {
    title?: string;
    archived?: boolean;
  },
  handler?: string
) {
  const params = handler ? { handler } : {};
  const response = await api.patch(`/chat/threads/${threadId}`, data, {
    params,
  });
  return response.data;
}

export async function deleteThread(threadId: string, handler?: string) {
  const params = handler ? { handler } : {};
  const response = await api.delete(`/chat/threads/${threadId}`, { params });
  return response.data;
}

export async function generateThreadTitle(
  threadId: string,
  message: string,
  handler?: string
) {
  const params = handler ? { handler } : {};
  const response = await api.post(
    `/chat/threads/${threadId}/title`,
    { message },
    { params }
  );
  return response.data;
}

// Chat Message APIs
export async function sendMessage(
  threadId: string,
  data: {
    role: string;
    content: any[];
    createdAt: string;
    parentId?: string;
    metadata?: any;
  },
  sendMessageHandler?: string
) {
  const params = sendMessageHandler
    ? { send_message_handler: sendMessageHandler }
    : {};
  const response = await api.post(`/chat/threads/${threadId}/messages`, data, {
    params,
  });
  return response;
}

export async function getThreadMessages(threadId: string, handler?: string) {
  const params = handler ? { handler } : {};
  const response = await api.get(`/chat/threads/${threadId}/messages`, {
    params,
  });
  return response;
}

// Like message API
export async function likeMessage(threadId: string, messageId: string) {
  const response = await api.post(
    `/chat/threads/${threadId}/messages/${messageId}/like`
  );
  return response.data;
}

// Dislike message API
export async function dislikeMessage(
  threadId: string,
  messageId: string,
  feedbackMessage: string = ""
) {
  const response = await api.post(
    `/chat/threads/${threadId}/messages/${messageId}/dislike`,
    {
      feedback_message: feedbackMessage,
    }
  );
  return response.data;
}

export async function searchFeedbacks(params: {
  feedback_type?: FeedbackTypeEnum;
  feedback_status?: FeedbackStatusEnum;
  skip?: number;
  take?: number;
  handler?: string;
  workspace_id?: string;
  agent_id?: string;
}) {
  const response = await api.get("/feedback/search", { params });
  return response.data;
}

export async function getFeedbackApprovedByAgent(params: {
  agent_id: string;
  feedback_type?: FeedbackTypeEnum;
  skip?: number;
  take?: number;
  handler?: string;
  workspace_id?: string;
}) {
  const response = await api.get("/feedback/approved", { params });
  return response.data;
}

export async function approveFeedback(feedbackId: string) {
  const response = await api.post(`/feedback/${feedbackId}/approve`);
  return response.data;
}

export async function rejectFeedback(feedbackId: string) {
  const response = await api.post(`/feedback/${feedbackId}/reject`);
  return response.data;
}

export async function deleteFeedback(feedbackId: string) {
  const response = await api.delete(`/feedback/${feedbackId}`);
  return response.data;
}

export async function getPendingFeedbackCount(params: {
  agent_id?: string | null;
  workspace_id?: string;
}) {
  const response = await api.get("/feedback/pending/count", { params });
  return response.data;
}

// Add this function to fetch individual feedback details
export async function fetchFeedbackById(feedbackId: string) {
  const response = await api.get(`/feedback/${feedbackId}`);
  return response.data;
}

// Add this function to fetch feedback types
export async function fetchFeedbackTypes() {
  const response = await api.get("/datasource/feedback-types");
  return response.data;
}

// Add this function to fetch feedback statuses
export async function fetchFeedbackStatuses() {
  const response = await api.get("/datasource/feedback-statuses");
  return response.data;
}

// Fetch subscription plan frequencies
export async function fetchSubscriptionFrequencies() {
  const response = await api.get("/datasource/subscription-frequencies");
  return response.data;
}

export async function getSuggestionQuestion(
  agent_id: string
): Promise<{ suggestions: string[] | null }> {
  try {
    const response = await api.get(`/agents/${agent_id}/suggestions`);
    return response.data;
  } catch (e) {
    console.error("Failed to fetch suggestions:", e);
    return null;
  }
}

export async function favoriteMessage(
  thread_id: string,
  messageId: string
): Promise<{
  success: boolean;
  suggestion_id: string;
  message: string;
}> {
  const response = await api.post(
    `/chat/threads/${thread_id}/messages/${messageId}/favourite`
  );
  return response.data;
}

export async function fetchAgentPipeline(
  agentId: string
): Promise<AgentPipelineResponse> {
  try {
    const response = await api.get(`/agents/${agentId}/pipelines`);
    return response.data;
  } catch (error) {
    console.warn("Falling back to empty pipeline data:", error);
    return { pipelines: [] };
  }
}

export async function updateAgentCron(
  agentId: string,
  cron: string,
  schedule_type: string
): Promise<UpdateAgentCronResponse> {
  const response = await api.patch<UpdateAgentCronResponse>(
    `/agents/${agentId}/cron`,
    {
      cron,
      schedule_type,
    }
  );

  return response.data;
}

export async function triggerAgentRun(agentId: string) {
  const response = await api.post(`/agents/${agentId}/runs/trigger`);
  return response.data;
}

export async function startAgentRun(agentId: string) {
  const response = await api.post(`/agents/${agentId}/runs/start`);
  return response.data;
}

export async function stopAgentRun(agentId: string) {
  const response = await api.post(`/agents/${agentId}/runs/stop`);
  return response.data;
}

// Agent Guidances API
export async function getAgentGuidances(
  agentId: string,
  params: AgentGuidancesParams = {}
): Promise<AgentGuidancesResponse> {
  const {
    skip = 0,
    take = 10,
    order_by = "created_at",
    order_direction = "desc",
    handler,
  } = params;

  const queryParams = {
    skip,
    take,
    order_by,
    order_direction,
    ...(handler && { handler }),
  };

  const response = await api.get(`/agents/${agentId}/guidances`, {
    params: queryParams,
  });
  return response.data;
}

export async function createAgentGuidance(
  agentId: string,
  data: CreateAgentGuidanceRequest
): Promise<AgentGuidance> {
  const response = await api.post(`/agents/${agentId}/guidances`, data);
  return response.data;
}

export async function updateAgentGuidance(
  agentId: string,
  guidanceId: string,
  data: CreateAgentGuidanceRequest
): Promise<AgentGuidance> {
  const response = await api.put(
    `/agents/${agentId}/guidances/${guidanceId}`,
    data
  );
  return response.data;
}

export async function deleteAgentGuidance(
  agentId: string,
  guidanceId: string
): Promise<void> {
  await api.delete(`/agents/${agentId}/guidances/${guidanceId}`);
}

export async function updateAgentPipeline(
  agentId: string,
  payload: UpdateAgentPipelinePayload
): Promise<UpdateAgentPipelineResponse> {
  const response = await api.patch<UpdateAgentPipelineResponse>(
    `/agents/${agentId}/pipelines`,
    payload
  );
  return response.data;
}

export async function getDocumentContent(documentId: string): Promise<{
  success: boolean;
  message: string;
  pages: {
    page_number: number;
    extraction_content: string;
  }[];
}> {
  const response = await api.get(`/documents/${documentId}/deep-extract`);
  return response.data;
}

export async function regenerateDocumentContent(
  documentId: string,
  instruction: string,
  page_number: number
): Promise<{ success: boolean; message: string; content: string }> {
  const response = await api.put(`/documents/${documentId}/deep-extract`, {
    instruction,
    page_number,
  });
  return response.data;
}

export async function saveDocumentContent(
  documentId: string,
  content: {
    page_number: number;
    extraction_content: string;
  }[]
): Promise<{ success: boolean; message: string }> {
  const response = await api.post(
    `/documents/${documentId}/deep-extract`,
    content
  );
  return response.data;
}
